import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProfileContext } from '../hooks/useProfileContext';
import { useAuth } from '../context/AuthContext';
import { useWriteGate } from '../lib/writeGate';
import { isShowcaseMode } from '../lib/saveStatus';
import { useConfig } from '../hooks/useConfig';
import CountryInput from '../components/CountryInput';
import AutoTextarea from '../components/AutoTextarea';
import { markWelcomeSeen } from '../lib/welcomeFlag';
import { reportSaveError } from '../lib/saveStatus';
import {
  apiCreateTeamMember,
  apiLinkTeamMemberBand,
  apiCreateVenue,
  apiCreateShow,
  apiGetMusicians,
  apiGetCrew,
} from '../api';
import { buildPdfDoc } from './RiderPage';
import { generatePdfBlobViaServer } from '../lib/pdf';
import type { Musician, CrewMember, TransportType } from '../types';
import PageHeader from '../components/PageHeader';

// The four rider transport presets a band can tour with.
const TRANSPORT_OPTIONS: { value: TransportType; labelKey: string }[] = [
  { value: 'van', labelKey: 'welcomeWizard.transportVan' },
  { value: 'tourbus', labelKey: 'welcomeWizard.transportTourbus' },
  { value: 'plane', labelKey: 'welcomeWizard.transportPlane' },
  { value: 'train', labelKey: 'welcomeWizard.transportTrain' },
];

const STEPS = [
  'welcomeWizard.stepBand',
  'welcomeWizard.stepTeam',
  'welcomeWizard.stepTransport',
  'welcomeWizard.stepFirstShow',
  'welcomeWizard.stepGenerateRider',
];

/* ------------------------------------------------------------------ */
/* Step 1 — Your band                                                  */
/* ------------------------------------------------------------------ */
function BandStep({ onDone, canWrite }: { onDone: (bandId: string) => void; canWrite: boolean }) {
  const { t } = useTranslation();
  const { activeProfileId, getProfile, createProfile, updateProfile } = useProfileContext();
  const existing = activeProfileId ? getProfile(activeProfileId) : null;
  const [name, setName] = useState(existing?.name || '');
  const [country, setCountry] = useState(existing?.country || '');
  const [image, setImage] = useState(existing?.image || '');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImage(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const save = () => {
    if (!canWrite) return; // read-only: never fire POST/PUT /api/bands
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    if (existing && activeProfileId) {
      updateProfile(activeProfileId, { name: trimmed, country: country.trim(), image });
      onDone(activeProfileId);
    } else {
      const profile = createProfile({ name: trimmed });
      updateProfile(profile.id, { country: country.trim(), image });
      onDone(profile.id);
    }
    setBusy(false);
  };

  return (
    <div className="card" style={{ padding: '20px 24px' }}>
      <h2 style={{ marginTop: 0 }}>{t('welcomeWizard.bandTitle')}</h2>
      <p className="text-dim">
        {t('welcomeWizard.bandHint')}
      </p>
      <div className="form-group">
        <label className="form-label" htmlFor="welcome-band-name">{t('welcomeWizard.bandName')}</label>
        <AutoTextarea
          id="welcome-band-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={existing ? t('welcomeWizard.bandNamePhExisting') : t('welcomeWizard.bandNamePh')}
        />
      </div>
      <div className="form-group">
        <label className="form-label">{t('welcomeWizard.homeCountry')}</label>
        <CountryInput value={country} onChange={setCountry} placeholder={t('welcomeWizard.countryPh')} />
      </div>
      <div className="form-group">
        <label className="form-label">{t('welcomeWizard.logoOptional')}</label>
        <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} style={{ display: 'none' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {image && (
            <img src={image} alt={t('welcomeWizard.bandTitle')} style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'contain', border: '1px solid var(--border)' }} />
          )}
          <button type="button" className="btn btn-secondary" onClick={() => fileRef.current?.click()}>
            {image ? t('welcomeWizard.changeLogo') : t('welcomeWizard.uploadLogo')}
          </button>
          {image && (
            <button type="button" className="btn btn-ghost" onClick={() => setImage('')}>
              {t('welcomeWizard.remove')}
            </button>
          )}
        </div>
      </div>
      <p className="text-dim" style={{ marginTop: 16, marginBottom: 0 }}>
        {existing ? t('welcomeWizard.existingSaved') : t('welcomeWizard.newBandCreated')}
      </p>
      <div style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-primary" disabled={!canWrite || !name.trim() || busy} onClick={save}>
          {t('welcomeWizard.continue')}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 2 — Your team                                                  */
/* ------------------------------------------------------------------ */
function TeamStep({ bandId, onDone, canWrite }: { bandId: string | null; onDone: () => void; canWrite: boolean }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<'musician' | 'crew'>('musician');
  const [added, setAdded] = useState<Array<{ id: string; name: string; category: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = useCallback(async () => {
    if (!canWrite) return; // read-only: never fire the team-member POSTs
    const trimmed = name.trim();
    if (!trimmed || !bandId) return;
    setBusy(true);
    setError(null);
    try {
      const member = await apiCreateTeamMember({
        name: trimmed,
        category,
        tags: [category],
        role: category === 'musician' ? 'musician' : 'crew',
        base_role: 'main',
        band_id: bandId,
      });
      // Linking mints the technical rider card (Musicians / Crew) so the person
      // shows up when generating the rider in step 5.
      await apiLinkTeamMemberBand(member.id, bandId, category);
      setAdded((prev) => [...prev, { id: member.id, name: trimmed, category }]);
      setName('');
    } catch (err) {
      reportSaveError('team', err);
      setError(err instanceof Error ? err.message : t('welcomeWizard.addPersonFailed'));
    } finally {
      setBusy(false);
    }
  }, [name, category, bandId]);

  return (
    <div className="card" style={{ padding: '20px 24px' }}>
      <h2 style={{ marginTop: 0 }}>{t('welcomeWizard.teamTitle')}</h2>
      <p className="text-dim">
        {t('welcomeWizard.teamHint')}
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="form-group" style={{ flex: 1, minWidth: 180 }}>
          <label className="form-label">{t('welcomeWizard.fullName')}</label>
          <AutoTextarea value={name} onChange={(e) => setName(e.target.value)} placeholder={t('welcomeWizard.fullNamePh')} />
        </div>
        <div className="form-group" style={{ minWidth: 140 }}>
          <label className="form-label">{t('welcomeWizard.role')}</label>
          <select className="form-input" value={category} onChange={(e) => setCategory(e.target.value as 'musician' | 'crew')}>
            <option value="musician">{t('welcomeWizard.musician')}</option>
            <option value="crew">{t('welcomeWizard.crew')}</option>
          </select>
        </div>
        <button type="button" className="btn btn-secondary" disabled={!canWrite || !name.trim() || busy} onClick={add}>
          {busy ? t('welcomeWizard.adding') : t('welcomeWizard.addPerson')}
        </button>
      </div>
      {error && <p style={{ color: 'var(--danger)', marginTop: 8 }}>{error}</p>}
      {added.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>{t('welcomeWizard.addedCount', { count: added.length })}</h3>
          {added.map((m) => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
              <span>{m.category === 'musician' ? '🎸' : '👥'}</span>
              <span>{m.name}</span>
              <span className="text-dim" style={{ fontSize: '0.82rem' }}>{m.category}</span>
            </div>
          ))}
          <p className="text-dim" style={{ fontSize: '0.85rem' }}>
            {t('welcomeWizard.teamTip')}
          </p>
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-primary" onClick={onDone}>
          {t('welcomeWizard.continue')}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 3 — Transport setup                                            */
/* ------------------------------------------------------------------ */
function TransportStep({
  config,
  updateRider,
  onDone,
}: {
  config: ReturnType<typeof useConfig>['config'];
  updateRider: (patch: Partial<ReturnType<typeof useConfig>['config']['rider']>) => void;
  onDone: (primary: string) => void;
}) {
  const { t } = useTranslation();
  const saved = config.rider?.transports ?? [];
  const [selected, setSelected] = useState<TransportType[]>(saved.length > 0 ? saved : []);
  const [primary, setPrimary] = useState<string>(config.rider?.transport || '');

  const toggle = (mode: TransportType) => {
    setSelected((prev) => (prev.includes(mode) ? prev.filter((m) => m !== mode) : [...prev, mode]));
  };

  const continue_ = () => {
    // The Continue button is disabled until at least one mode is chosen, so
    // `selected` is non-empty here. Never default to 'van' (Defaults-Spec P8).
    const modes: TransportType[] = selected;
    const finalPrimary: TransportType = modes.includes(primary as TransportType)
      ? (primary as TransportType)
      : modes[0];
    updateRider({ transports: modes, transport: finalPrimary });
    onDone(finalPrimary);
  };

  return (
    <div className="card" style={{ padding: '20px 24px' }}>
      <h2 style={{ marginTop: 0 }}>{t('welcomeWizard.transportTitle')}</h2>
      <p className="text-dim">
        {t('welcomeWizard.transportHint')}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {TRANSPORT_OPTIONS.map((m) => (
          <label key={m.value} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={selected.includes(m.value)}
              onChange={() => toggle(m.value)}
            />
            <span>{t(m.labelKey)}</span>
          </label>
        ))}
      </div>
      <div className="form-group" style={{ marginTop: 16 }}>
        <label className="form-label">{t('welcomeWizard.defaultTransport')}</label>
        <select className="form-input" value={primary} onChange={(e) => setPrimary(e.target.value)} disabled={selected.length === 0}>
          <option value="" disabled>{t('welcomeWizard.selectTransport')}</option>
          {TRANSPORT_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>{t(m.labelKey)}</option>
          ))}
        </select>
      </div>
      <div style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-primary" onClick={continue_} disabled={selected.length === 0}>
          {t('welcomeWizard.continue')}
        </button>
        {selected.length === 0 && (
          <p className="text-dim" style={{ marginTop: 8, marginBottom: 0 }}>
            {t('welcomeWizard.tickOne')}
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 4 — First show                                                 */
/* ------------------------------------------------------------------ */
function FirstShowStep({ bandId, transport, onDone, canWrite }: { bandId: string | null; transport: string; onDone: (showId: string) => void; canWrite: boolean }) {
  const { t } = useTranslation();
  const [venueName, setVenueName] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!canWrite) return; // read-only: never fire the venue / show POSTs
    if (!venueName.trim() || !bandId) return;
    setBusy(true);
    setError(null);
    try {
      const venue = await apiCreateVenue({ name: venueName.trim(), city: city.trim(), country: country.trim() });
      const show = await apiCreateShow({
        band_id: bandId,
        venue_id: venue.id,
        name: venueName.trim(),
        date: date || undefined,
        status: 'confirmed',
        event_type: 'show',
        transport,
      });
      onDone(String(show.id));
    } catch (err) {
      reportSaveError('show', err);
      setError(err instanceof Error ? err.message : t('welcomeWizard.createShowFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: '20px 24px' }}>
      <h2 style={{ marginTop: 0 }}>{t('welcomeWizard.firstShowTitle')}</h2>
      <p className="text-dim">
        {t('welcomeWizard.firstShowHint')}
      </p>
      <div className="form-group">
        <label className="form-label">{t('welcomeWizard.venueName')}</label>
        <AutoTextarea value={venueName} onChange={(e) => setVenueName(e.target.value)} placeholder={t('welcomeWizard.venueNamePh')} />
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
          <label className="form-label">{t('welcomeWizard.city')}</label>
          <AutoTextarea value={city} onChange={(e) => setCity(e.target.value)} placeholder={t('welcomeWizard.cityPh')} />
        </div>
        <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
          <label className="form-label">{t('welcomeWizard.country')}</label>
          <CountryInput value={country} onChange={setCountry} placeholder={t('welcomeWizard.countryPh')} />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">{t('welcomeWizard.showDate')}</label>
        <input type="date" className="form-input" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      {error && <p style={{ color: 'var(--danger)', marginTop: 8 }}>{error}</p>}
      <div style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-primary" disabled={!canWrite || !venueName.trim() || !date || busy} onClick={save}>
          {busy ? t('welcomeWizard.creating') : t('welcomeWizard.createShow')}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 5 — Generate your rider                                        */
/* ------------------------------------------------------------------ */
function RiderStep({
  bandId,
  bandName,
  config,
  updateRider,
  onDone,
}: {
  bandId: string | null;
  bandName: string | null;
  config: ReturnType<typeof useConfig>['config'];
  updateRider: (patch: Partial<ReturnType<typeof useConfig>['config']['rider']>) => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [musicians, setMusicians] = useState<Musician[]>([]);
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [selMus, setSelMus] = useState<string[]>([]);
  const [selCrew, setSelCrew] = useState<string[]>([]);
  const [transport, setTransport] = useState<string>(config.rider?.transport || '');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bandId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([apiGetMusicians(bandId).catch(() => null), apiGetCrew(bandId).catch(() => null)])
      .then(([mus, cr]) => {
        if (cancelled) return;
        const music = (mus || []) as Musician[];
        const crewList = (cr || []) as CrewMember[];
        setMusicians(music);
        setCrew(crewList);
        // Pre-select everyone who already has a card (the core lineup).
        setSelMus((prev) => (prev.length > 0 ? prev : music.map((m) => m.id)));
        setSelCrew((prev) => (prev.length > 0 ? prev : crewList.map((c) => c.id)));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [bandId]);

  const toggleMus = (id: string) =>
    setSelMus((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleCrew = (id: string) =>
    setSelCrew((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const generate = async () => {
    if (!bandId) return;
    if (!transport) {
      setError(t('welcomeWizard.generateNoTransport'));
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const mus = musicians.filter((m) => selMus.includes(m.id));
      const cr = crew.filter((c) => selCrew.includes(c.id));
      const docDef = buildPdfDoc(
        bandName ?? undefined,
        { transport, activeMusicianIds: selMus, activeCrewIds: selCrew, requirements: null },
        config,
        mus,
        cr,
        null,
        null,
        null,
      );
      // Persist the selection so the Rider page opens on the same lineup.
      updateRider({ transport: transport as TransportType, activeMusicianIds: selMus, activeCrewIds: selCrew });
      const blobUrl = await generatePdfBlobViaServer(docDef);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'technical-rider.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      onDone();
    } catch (err) {
      reportSaveError('rider', err);
      setError(err instanceof Error ? err.message : t('welcomeWizard.generateFailed'));
      setGenerating(false);
    }
  };

  const people: Array<{ id: string; name: string; selected: boolean; toggle: () => void; emoji: string }> = [
    ...musicians.map((m) => ({ id: m.id, name: m.name || m.id, selected: selMus.includes(m.id), toggle: () => toggleMus(m.id), emoji: '🎸' })),
    ...crew.map((c) => ({ id: c.id, name: c.name || c.id, selected: selCrew.includes(c.id), toggle: () => toggleCrew(c.id), emoji: '👥' })),
  ];

  return (
    <div className="card" style={{ padding: '20px 24px' }}>
      <h2 style={{ marginTop: 0 }}>{t('welcomeWizard.riderTitle')}</h2>
      <p className="text-dim">
        {t('welcomeWizard.riderHint')}
      </p>
      <div className="form-group">
        <label className="form-label">{t('welcomeWizard.transport')}</label>
        <select className="form-input" value={transport} onChange={(e) => setTransport(e.target.value)}>
          <option value="" disabled>{t('welcomeWizard.selectTransport')}</option>
          {TRANSPORT_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>{t(m.labelKey)}</option>
          ))}
        </select>
      </div>
      {loading ? (
        <p className="text-dim">{t('welcomeWizard.loadingPeople')}</p>
      ) : people.length === 0 ? (
        <p className="text-dim">
          {t('welcomeWizard.noPeople')}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
          {people.map((p) => (
            <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={p.selected} onChange={p.toggle} />
              <span>{p.emoji}</span>
              <span>{p.name}</span>
            </label>
          ))}
        </div>
      )}
      {error && <p style={{ color: 'var(--danger)', marginTop: 8 }}>{error}</p>}
      <div style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-primary" disabled={generating || !transport} onClick={generate}>
          {generating ? t('welcomeWizard.generating') : t('welcomeWizard.generateDownload')}
        </button>
        <button type="button" className="btn btn-secondary" style={{ marginLeft: 8 }} onClick={onDone}>
          {t('welcomeWizard.finishWithout')}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Wizard shell                                                        */
/* ------------------------------------------------------------------ */
export default function WelcomeWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeProfileId, getProfile } = useProfileContext();
  const { user } = useAuth();
  // ── Write gate (A2, 2026-09-25) — AUTHORITY writes, plain `canWrite` ───────
  // Steps 1/2/4 write to routes mounted AFTER the server's global read-only
  // guard (`/api/bands` POST/PUT, `/api/team`, `/api/venues`, `/api/shows`;
  // server/index.js:572 /api/bands, :596 /api/venues, :600 /api/team,
  // :630 /api/shows, vs the guard at :519-525) and delegable to no
  // capability → the exact freeze is the ACCOUNT ceiling. The band-scoped
  // `writeGate.canWrite` would also freeze an account that has NO band yet —
  // precisely the user this wizard exists for (step 1 creates the first band) —
  // and over-freezing a legal action is a bug of the same class as letting an
  // impossible one through. The demo is excluded like everywhere else (its
  // writes are cut in `fetchJSON`; the global <ShowcaseBanner/> explains it).
  // Steps 3/5 only PERSIST through `useConfig` (`PUT /api/bands/:id/rider/config`),
  // which already gates its own autosave on the exact `content.rider` capability
  // — they are deliberately NOT frozen here (a read-only member holding
  // `content.rider` legitimately keeps them, and freezing them would also block
  // the rider PDF the wizard just generated).
  // NB: gated at the page (the caller) on purpose: `useAuth()` THROWS inside
  // `useProfileContext` — `App.tsx` nests <AuthProvider> INSIDE <ProfileProvider>
  // — so the hook's create/update/deleteProfile stay ungated.
  const writeGate = useWriteGate();
  const isShowcase = !!user?.is_showcase || isShowcaseMode();
  const canWrite = !writeGate.readOnlyReason && !isShowcase;
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !canWrite;
  const { config, updateRider } = useConfig();
  const [step, setStep] = useState(0);
  const [bandId, setBandId] = useState<string | null>(activeProfileId);
  const [primaryTransport, setPrimaryTransport] = useState<string>('');

  const profile = activeProfileId ? getProfile(activeProfileId) : null;
  const total = STEPS.length;

  const next = () => {
    if (step < total - 1) setStep(step + 1);
    else finish();
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const finish = () => {
    markWelcomeSeen();
    navigate(activeProfileId ? '/tour/shows' : '/home', { replace: true });
  };

  const skipStep = () => next();
  const skipAll = () => finish();

  // Sub-component onDone callbacks inject data into the shell.
  const handleBand = (id: string) => { setBandId(id); next(); };
  const handleTeam = () => next();
  const handleTransport = (primary: string) => { setPrimaryTransport(primary); next(); };
  const handleShow = () => next();
  const handleRiderDone = () => finish();

  useEffect(() => {
    // After the band is created in step 1 it becomes active; keep bandId in sync.
    if (activeProfileId) setBandId(activeProfileId);
  }, [activeProfileId]);

  return (
    <div className="page" data-page="welcome">
      <PageHeader
        title={t('welcomeWizard.title')}
        subtitle={t('welcomeWizard.subtitle', { count: total })}
      />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STEPS.map((label, i) => (
            <span
              key={label}
              className={`welcome-step-dot ${i === step ? 'active' : i < step ? 'done' : ''}`}
              style={{
                padding: '4px 12px',
                borderRadius: 999,
                fontSize: '0.82rem',
                background: i === step ? 'var(--accent)' : i < step ? 'var(--accent-tint-strong)' : 'transparent',
                color: i === step ? 'var(--on-accent)' : i < step ? 'var(--accent)' : 'var(--text-dim)',
                border: '1px solid var(--border)',
              }}
            >
              {i + 1}. {t(label)}
            </span>
          ))}
        </div>
        <button type="button" className="btn btn-ghost btn-small" onClick={skipAll}>
          {t('welcomeWizard.skipTour')}
        </button>
      </div>

      {step === 0 && <BandStep onDone={handleBand} canWrite={canWrite} />}
      {step === 1 && <TeamStep bandId={bandId} onDone={handleTeam} canWrite={canWrite} />}
      {step === 2 && <TransportStep config={config} updateRider={updateRider} onDone={handleTransport} />}
      {step === 3 && <FirstShowStep bandId={bandId} transport={primaryTransport} onDone={handleShow} canWrite={canWrite} />}
      {step === 4 && (
        <RiderStep bandId={bandId} bandName={profile?.name ?? null} config={config} updateRider={updateRider} onDone={handleRiderDone} />
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
        {step > 0 && (
          <button type="button" className="btn btn-secondary" onClick={back}>
            {t('welcomeWizard.back')}
          </button>
        )}
        {step < total - 1 && step !== 2 && (
          <button type="button" className="btn btn-ghost" onClick={skipStep}>
            {t('welcomeWizard.skipStep')}
          </button>
        )}
        {step === total - 1 && (
          <button type="button" className="btn btn-ghost" onClick={finish}>
            {t('welcomeWizard.finishLater')}
          </button>
        )}
      </div>
    </div>
  );
}